/**
 * C-01 (NC-01) + C-05 · Validación local PURA de rúbricas polimórficas.
 *
 * Justificación: la validación vivía dentro de GestionRubricasView (no
 * testeable) y solo conocía la forma genérica "niveles". Se extrae a función
 * pura (testeable con Vitest, C-05) y se valida según tipo_informacion,
 * espejo de CriterioRubricaService del backend.
 */

import type {
  RubricaCriterio,
  RubricaDocumental,
  RubricaCuantitativa,
  RubricaOpinion,
  TipoInformacion
} from '@/interfaces/CriterioEvaluacion'

interface Rango {
  rango_min: number
  rango_max: number
}

/** Valida que un conjunto de rangos esté en 0–5, min<max y sin solapamientos. */
export function validarRangos(rangos: Rango[]): string | null {
  for (const r of rangos) {
    if (typeof r.rango_min !== 'number' || typeof r.rango_max !== 'number' || isNaN(r.rango_min) || isNaN(r.rango_max)) {
      return 'Los rangos deben ser numéricos.'
    }
    if (r.rango_min < 0 || r.rango_max > 5) return 'Los rangos deben estar entre 0 y 5.'
    if (r.rango_min >= r.rango_max) return 'rango_min debe ser menor que rango_max.'
  }
  const sorted = [...rangos].sort((a, b) => a.rango_min - b.rango_min)
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].rango_min < sorted[i - 1].rango_max) {
      return 'Los rangos no deben solaparse.'
    }
  }
  return null
}

/**
 * Valida una rúbrica DOCUMENTAL: matriz [grado × criterio] de descriptores.
 *
 * Exige que la matriz esté COMPLETA — cada grado debe tener descriptor para cada
 * criterio—, porque una celda vacía deja al calificador sin referencia
 * precisamente en el caso que tenga delante.
 *
 * Complejidad: O(g × c), con g grados y c criterios; es el recorrido íntegro de
 * la matriz.
 *
 * @returns `null` si la rúbrica es válida, o el primer error en texto listo para
 *          mostrar. Solo se devuelve el primero: la validación corta al primer
 *          fallo.
 */
export function validarRubricaDocumental(r: RubricaDocumental): string | null {
  if (!r.criterios?.length) return 'Defina al menos un criterio (p. ej. Coherencia, Claridad…).'
  if (r.criterios.some(c => !c?.trim())) return 'Ningún criterio puede estar vacío.'
  if (!r.grados?.length) return 'Agregue al menos un grado a la matriz documental.'
  for (const g of r.grados) {
    if (!g.grado?.trim()) return 'Cada grado debe tener nombre.'
    for (const c of r.criterios) {
      if (!g.descriptores?.[c]?.trim()) {
        return `El grado "${g.grado}" no tiene descriptor para "${c}".`
      }
    }
  }
  return validarRangos(r.grados)
}

/**
 * Valida una rúbrica CUANTITATIVA: niveles con rango y etiqueta.
 *
 * Acepta la etiqueta en `grado` o en `label` (dos nombres históricos del mismo
 * campo), y delega en `validarRangos` la comprobación de que los rangos no se
 * solapen — un solapamiento haría que una misma nota cayera en dos niveles.
 *
 * @returns `null` si es válida, o el primer error en texto.
 */
export function validarRubricaCuantitativa(r: RubricaCuantitativa): string | null {
  if (!r.niveles?.length) return 'Agregue al menos un nivel.'
  for (const n of r.niveles) {
    const etiqueta = n.grado ?? n.label
    if (!etiqueta?.trim()) return 'Cada nivel debe tener grado/etiqueta.'
  }
  return validarRangos(r.niveles)
}

/**
 * Valida una rúbrica de OPINIÓN.
 *
 * Es la de reglas más laxas de las tres: `conversion` es obligatoria siempre —sin
 * ella no hay forma de llevar las respuestas a la escala 0–5—, pero de `muestreo`
 * y `grados` basta con UNO. Son dos formas legítimas de sustentar el resultado:
 * por representatividad de la muestra o por la tabla de valoración del MAAM.
 *
 * @returns `null` si es válida, o el primer error en texto.
 */
export function validarRubricaOpinion(r: RubricaOpinion): string | null {
  const tieneMuestreo = !!r.muestreo?.length
  const tieneGrados = !!r.grados?.length

  // Basta con uno de los dos, además de la conversión (siempre obligatoria).
  if (!tieneMuestreo && !tieneGrados) {
    return 'Agregue al menos un grupo de interés al muestreo o una fila a la tabla de valoración.'
  }

  if (tieneMuestreo) {
    for (const m of r.muestreo!) {
      if (!m.grupo_interes?.trim()) return 'Cada fila de muestreo debe indicar el grupo de interés.'
      if (!m.tipo_muestreo?.trim()) return 'Cada fila de muestreo debe indicar el tipo de muestreo.'
      if (!m.tecnica?.trim()) return 'Cada fila de muestreo debe indicar la técnica.'
    }
  }

  if (tieneGrados) {
    for (const g of r.grados!) {
      if (!g.grado?.trim()) return 'Cada fila de la tabla de valoración debe tener grado.'

      const tieneMin = g.pct_min !== undefined && g.pct_min !== null
      const tieneMax = g.pct_max !== undefined && g.pct_max !== null
      if (tieneMin !== tieneMax) {
        return `La fila "${g.grado}" debe indicar el cumplimiento % completo (mínimo y máximo) o ninguno.`
      }
      if (tieneMin && tieneMax) {
        const min = Number(g.pct_min)
        const max = Number(g.pct_max)
        if (isNaN(min) || isNaN(max)) return `La fila "${g.grado}" tiene el cumplimiento % no numérico.`
        if (min < 0 || max > 100) return `El cumplimiento % de "${g.grado}" debe estar entre 0 y 100.`
        if (min >= max) return `En "${g.grado}", el cumplimiento % mínimo debe ser menor que el máximo.`
      }
    }

    const errorRangos = validarRangos(r.grados!)
    if (errorRangos) return errorRangos
  }

  if (!r.conversion?.formula?.trim()) return 'Defina la fórmula de conversión del resultado a 0–5.'
  if (!r.conversion?.escala?.trim()) return 'Defina la escala de conversión (p. ej. "porcentaje → 0-5").'
  return null
}

/** Punto de entrada polimórfico (espejo del backend). */
export function validarRubrica(tipo: TipoInformacion | '', criterio: RubricaCriterio): string | null {
  switch (tipo) {
    case 'documental':
      return validarRubricaDocumental(criterio as RubricaDocumental)
    case 'cuantitativa':
      return validarRubricaCuantitativa(criterio as RubricaCuantitativa)
    case 'opinion':
      return validarRubricaOpinion(criterio as RubricaOpinion)
    default:
      return 'Seleccione un tipo de información.'
  }
}
